import React, { useState, useRef } from "react";
import WebLayout from "../components/WebLayout";
import { toast } from "sonner";
import { CheckCircle2, Coins, Target, MapPin, AlertCircle, Loader2 } from "lucide-react";
import TurnstileWidget from "../components/TurnstileWidget";
import Seo from "../components/Seo";

const HEARD_FROM = ["TikTok", "YouTube", "Instagram", "Friend / Referral", "Podcast", "Google Search", "Other"];
const API = process.env.REACT_APP_BACKEND_URL || "";
function readCookie(name) {
  try { return decodeURIComponent(document.cookie.split("; ").find((c) => c.startsWith(name + "="))?.split("=")[1] || ""); } catch { return ""; }
}

export default function ScoutsLanding() {
  const [form, setForm] = useState({
    firstName: "", lastName: "", email: "", phone: "", zip: "", state: "",
    heard: "", referral: "", website_hp: "",
  });
  const [submitted, setSubmitted] = useState(false);
  const [alreadyRegistered, setAlreadyRegistered] = useState(false);
  const [ownCode, setOwnCode] = useState("");
  const [ownLink, setOwnLink] = useState("");
  const [loading, setLoading] = useState(false);
  const [banner, setBanner] = useState("");
  const [fieldErrors, setFieldErrors] = useState({});
  const [tsToken, setTsToken] = useState("");
  const [consent, setConsent] = useState(false);
  const tsRef = useRef(null);
  const tsRequired = !!process.env.REACT_APP_TURNSTILE_SITE_KEY;

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  async function handleSubmit(e) {
    e.preventDefault();
    if (tsRequired && !tsToken) {
      setBanner("Please complete the verification challenge and try again.");
      return;
    }
    setLoading(true); setBanner(""); setFieldErrors({});
    try {
      const r = await fetch(`${API}/api/preregister/scout`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          first_name: form.firstName, last_name: form.lastName, email: form.email,
          phone: form.phone, zip: form.zip, state: form.state.toUpperCase(),
          how_heard: form.heard || null,
          referral_code: form.referral || readCookie("sb_ref") || null,
          website_hp: form.website_hp,
          turnstile_token: tsToken,
        }),
      });
      const data = await r.json().catch(() => ({}));
      if (r.status === 200 && data.status === "created") {
        setSubmitted(true);
        setOwnCode(data.own_referral_code || "");
        setOwnLink(data.own_referral_link || "");
        toast.success("You're on the Scout waitlist.");
      }
      else if (r.status === 200 && data.status === "already_registered") { setSubmitted(true); setAlreadyRegistered(true); toast.success("Already on the list."); }
      else if (r.status === 422 && data.status === "turnstile_failed") {
        setBanner("Verification failed. Please try again.");
        setTsToken(""); tsRef.current?.reset();
      }
      else if (r.status === 422) {
        const fe = {}; (data.errors || []).forEach((x) => { fe[x.field] = x.message; });
        setFieldErrors(fe); setBanner("Please fix the highlighted fields.");
        setTsToken(""); tsRef.current?.reset();
      } else if (r.status === 429) { setBanner("Too many attempts. Please try again later."); setTsToken(""); tsRef.current?.reset(); }
      else { setBanner("Something went wrong. Please try again."); setTsToken(""); tsRef.current?.reset(); }
    } catch { setBanner("Something went wrong. Please try again."); setTsToken(""); tsRef.current?.reset(); }
    finally { setLoading(false); }
  }

  return (
    <WebLayout>
      <Seo title="For Scouts" description="Join SpotBounty's Scout waitlist and earn bounties when you submit vending-ready locations that sell." path="/scouts" />
      <section className="max-w-7xl mx-auto px-6 py-16 grid lg:grid-cols-[1.1fr_1fr] gap-14">
        <div>
          <div className="sb-eyebrow flex items-center gap-2">
            <Target size={14} className="text-[#FFC107]" /> For Scouts
          </div>
          <h1 className="sb-headline mt-5 text-[48px] md:text-[64px]">
            FIND SPOTS. <br />GET <span className="text-[#FFC107]">PAID.</span>
          </h1>
          <p className="mt-5 text-[#A1A1AA] max-w-lg leading-relaxed">
            Turn your daily walk, commute, or side hustle into real income. Submit vending-ready locations to SpotBounty
            and earn a bounty every time a location you sourced sells to an independent operator.
          </p>

          <div className="mt-8 grid sm:grid-cols-2 gap-4">
            <div className="sb-card p-5">
              <Coins size={20} className="text-[#FFC107]" />
              <div className="mt-3 font-black text-[22px]">Up to $450</div>
              <div className="text-[13px] text-[#A1A1AA]">Per verified location that sells</div>
            </div>
            <div className="sb-card p-5">
              <MapPin size={20} className="text-[#FFC107]" />
              <div className="mt-3 font-black text-[22px]">150+ Cities</div>
              <div className="text-[13px] text-[#A1A1AA]">Active bounty campaigns nationwide</div>
            </div>
          </div>

          <div className="mt-8 sb-card p-5">
            <div className="sb-eyebrow">Example bounty</div>
            <div className="mt-2 text-[14px] text-white/90 leading-relaxed">
              You submit a strong warehouse location. Once verified, it lists for $999. When an operator purchases it, you earn about a <span className="text-[#FFC107] font-bold">$250 bounty</span>.
            </div>
            <div className="mt-4 pt-4 border-t border-[#2A2F34] text-[12.5px] text-[#A1A1AA] leading-relaxed">
              Bounties vary. Preregistration does not earn a bounty. Eligibility and payment require a qualifying verified location sale under the program terms in effect when the bounty program is live.
            </div>
          </div>
        </div>

        <div>
          {!submitted ? (
            <form data-testid="scout-waitlist-form" onSubmit={handleSubmit} className="sb-card p-6 md:p-8 space-y-4">
              <div>
                <div className="sb-eyebrow">Join Waitlist</div>
                <h2 className="text-[28px] font-black mt-2">Get scouting access.</h2>
              </div>
              {banner && (
                <div className="sb-card p-3 border-[#EF4444]/40 flex items-start gap-2" style={{ background: "rgba(239,68,68,.06)" }}>
                  <AlertCircle size={14} className="text-[#EF4444] mt-0.5" />
                  <span className="text-[12.5px] text-[#EF4444]">{banner}</span>
                </div>
              )}
              <div className="grid grid-cols-2 gap-3">
                <div><label className="sb-label">First Name</label><input required data-testid="scout-firstname" className="sb-input" value={form.firstName} onChange={set("firstName")} />{fieldErrors.first_name && <div className="text-[11px] text-[#EF4444] mt-1">{fieldErrors.first_name}</div>}</div>
                <div><label className="sb-label">Last Name</label><input required data-testid="scout-lastname" className="sb-input" value={form.lastName} onChange={set("lastName")} />{fieldErrors.last_name && <div className="text-[11px] text-[#EF4444] mt-1">{fieldErrors.last_name}</div>}</div>
              </div>
              <div><label className="sb-label">Email</label><input required type="email" data-testid="scout-email" className="sb-input" value={form.email} onChange={set("email")} />{fieldErrors.email && <div className="text-[11px] text-[#EF4444] mt-1">{fieldErrors.email}</div>}</div>
              <div><label className="sb-label">Mobile Phone</label><input required data-testid="scout-phone" className="sb-input" value={form.phone} onChange={set("phone")} />{fieldErrors.phone && <div className="text-[11px] text-[#EF4444] mt-1">{fieldErrors.phone}</div>}</div>
              <div className="grid grid-cols-2 gap-3">
                <div><label className="sb-label">ZIP</label><input required data-testid="scout-zip" className="sb-input" value={form.zip} onChange={set("zip")} />{fieldErrors.zip && <div className="text-[11px] text-[#EF4444] mt-1">{fieldErrors.zip}</div>}</div>
                <div><label className="sb-label">State</label><input required maxLength={2} data-testid="scout-state" className="sb-input uppercase" value={form.state} onChange={set("state")} />{fieldErrors.state && <div className="text-[11px] text-[#EF4444] mt-1">{fieldErrors.state}</div>}</div>
              </div>
              <div>
                <label className="sb-label">How did you hear about SpotBounty?</label>
                <select data-testid="scout-heard" className="sb-input" value={form.heard} onChange={set("heard")}>
                  <option value="">Select…</option>
                  {HEARD_FROM.map((h) => <option key={h} value={h}>{h}</option>)}
                </select>
              </div>
              <div><label className="sb-label">Optional Referral Code</label><input data-testid="scout-referral" className="sb-input" value={form.referral} onChange={set("referral")} placeholder="e.g. ALEXJ25" /></div>
              {/* honeypot */}
              <input type="text" name="website_hp" value={form.website_hp} onChange={set("website_hp")} tabIndex={-1} autoComplete="off" style={{ position: "absolute", left: "-9999px", width: 1, height: 1, opacity: 0 }} />

              <TurnstileWidget ref={tsRef} onToken={setTsToken} testId="scout-turnstile" />

              <label htmlFor="scout-consent" className="flex items-start gap-2 text-[12px] text-[#A1A1AA] leading-relaxed cursor-pointer">
                <input id="scout-consent" type="checkbox" checked={consent} onChange={(e) => setConsent(e.target.checked)} data-testid="scout-consent" className="mt-[3px] w-4 h-4 accent-[#FFC107] shrink-0" required />
                <span>I agree to the <a href="/terms" className="text-[#FFC107] underline">Terms of Service</a> and acknowledge the <a href="/privacy" className="text-[#FFC107] underline">Privacy Policy</a>.</span>
              </label>
              <button type="submit" disabled={loading || (tsRequired && !tsToken) || !consent} data-testid="scout-submit" className="sb-btn-primary w-full disabled:opacity-50">
                {loading ? <><Loader2 size={16} className="animate-spin" /> Sending…</> : "Join Scout Waitlist"}
              </button>
            </form>
          ) : (
            <div data-testid="scout-confirmation" className="sb-card p-10 text-center">
              <CheckCircle2 size={48} className="text-[#FFC107] mx-auto" />
              <div className="mt-5 sb-eyebrow">Confirmation</div>
              <h2 className="text-[32px] font-black mt-3">YOU'RE ON THE LIST.</h2>
              <p className="mt-4 text-[#A1A1AA] max-w-md mx-auto">
                {alreadyRegistered
                  ? "You're already on the list — thanks for reconfirming your interest."
                  : "We'll notify you when SpotBounty scouting opens in your area."}
              </p>
              {ownCode && (
                <div className="mt-6 pt-6 border-t border-[#2A2F34] text-left" data-testid="scout-own-code">
                  <div className="sb-eyebrow" style={{ fontSize: 10 }}>Your pre-launch referral code</div>
                  <div className="mt-2 text-[22px] font-black tracking-widest text-[#FFC107] text-center sb-mono">{ownCode}</div>
                  <div className="mt-2 sb-mono text-[12px] text-[#A1A1AA] text-center break-all">{ownLink}</div>
                  <p className="mt-4 text-[12px] text-[#A1A1AA] leading-relaxed">
                    Share your code with friends. We'll preserve your pre-launch referral attribution — if SpotBounty launches
                    and the future referral program applies, we'll honor the attribution we tracked for you.
                  </p>
                </div>
              )}
            </div>
          )}
        </div>
      </section>
    </WebLayout>
  );
}

